import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import ShareOptionsModalV2 from '../../Share/ShareOptionsModalV2/ShareOptionsModalV2';
import OolaWheelMilestoneV2 from '../../PortalV2/Steps/OolaWheelMilestoneV2';
import './OolaScorePanel.css';

/** Map GetOolaScores areas → Fs for OolaWheelMilestoneV2 (TotalScore is 0–100). */
function areasToFs(areas) {
    if (!Array.isArray(areas)) return [];
    return areas.map((a) => ({
        id: a.id ?? a.Id,
        areaId: a.id ?? a.Id,
        name: a.name ?? a.Name,
        color: a.color ?? a.Color,
        TotalScore: Number(
            a.totalScore ??
                a.TotalScore ??
                a.score ??
                a.Score ??
                a.calculatedScore ??
                a.CalculatedScore ??
                0
        ),
    }));
}

/**
 * V2 Profile — My Oola Score (same API as classic dashboard MyOolaScore).
 * Does not modify V1 Dashboard / MyOolaScore components.
 */
const OolaScorePanel = ({ active = true, onLatestScore }) => {
    const { checkUserSession } = useContext(AppContext);
    const { token } = checkUserSession();

    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [selected, setSelected] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);

    const loadScores = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setErrorMsg('');
        const { error, data } = await requestAxios({
            url: 'Dashboard/GetOolaScores',
            token,
        });
        if (!error && Array.isArray(data)) {
            setScores(data.filter((x) => Number(x?.score) > 0));
        } else {
            setErrorMsg('Could not load Oola scores.');
            setScores([]);
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        if (!active) return;
        loadScores();
    }, [active, loadScores]);

    const latest = useMemo(() => scores[0] || null, [scores]);
    const selectedFs = useMemo(
        () => (selected ? areasToFs(selected.areas) : []),
        [selected]
    );

    useEffect(() => {
        if (typeof onLatestScore !== 'function') return;
        onLatestScore(latest?.score ?? null);
    }, [latest, onLatestScore]);

    return (
        <div className="dv2-oscore">
            <header className="dv2-oscore__header">
                <div>
                    <h2 className="dv2-oscore__title">My Oola Score</h2>
                    <p className="dv2-oscore__subtitle">
                        Your Oola Wheel results across life areas
                    </p>
                </div>
            </header>

            {loading ? (
                <p className="dv2-oscore__status">Loading scores…</p>
            ) : null}
            {errorMsg ? <p className="dv2-oscore__error">{errorMsg}</p> : null}

            {!loading && !errorMsg && scores.length === 0 ? (
                <p className="dv2-oscore__status">
                    No Oola Wheel scores yet. Complete your assessment to see
                    results here.
                </p>
            ) : null}

            {!loading && scores.length > 0 ? (
                <div className="dv2-oscore__strip" role="list">
                    {scores.map((item, index) => {
                        const isLatest = index === 0;
                        return (
                            <button
                                key={`oscore-${item.completionDate || index}-${item.score}`}
                                type="button"
                                role="listitem"
                                className={`dv2-oscore__card${
                                    isLatest ? ' is-latest is-featured' : ''
                                }`}
                                onClick={() => setSelected(item)}
                                aria-label={`Oola score ${item.score}${
                                    item.completionDate
                                        ? `, ${item.completionDate}`
                                        : ''
                                }. Open wheel.`}
                            >
                                <span className="dv2-oscore__card-wheel" aria-hidden>
                                    <OolaWheelMilestoneV2
                                        Fs={areasToFs(item.areas)}
                                        compact
                                        celebrate
                                        hideFooter
                                        hubScore={item.score}
                                    />
                                </span>
                                <span className="dv2-oscore__card-scoreblock">
                                    <span className="dv2-oscore__card-score">
                                        {item.score}
                                    </span>
                                    {isLatest ? (
                                        <span className="dv2-oscore__card-kicker">
                                            Latest score
                                        </span>
                                    ) : (
                                        <span className="dv2-oscore__card-date">
                                            {item.completionDate || ''}
                                        </span>
                                    )}
                                </span>
                                <span className="dv2-oscore__card-meta">
                                    <span className="dv2-oscore__card-date">
                                        {item.completionDate || ''}
                                    </span>
                                    {isLatest ? (
                                        <span className="dv2-oscore__card-cta">
                                            View your Oola Wheel →
                                        </span>
                                    ) : null}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {latest && !loading && scores.length > 0 ? (
                <p className="dv2-oscore__hint">
                    Your latest life balance assessment
                </p>
            ) : null}

            {selected ? (
                <div
                    className="dv2-oscore__modal"
                    role="presentation"
                    onClick={() => {
                        setShareOpen(false);
                        setSelected(null);
                    }}
                >
                    <div
                        className="dv2-oscore__modal-card"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="dv2-oscore-modal-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="dv2-oscore__modal-close"
                            aria-label="Close"
                            onClick={() => {
                                setShareOpen(false);
                                setSelected(null);
                            }}
                        >
                            ×
                        </button>
                        <h3
                            id="dv2-oscore-modal-title"
                            className="dv2-oscore__modal-title"
                        >
                            Oola Wheel
                        </h3>
                        {selected.completionDate ? (
                            <p className="dv2-oscore__modal-date">
                                {selected.completionDate}
                            </p>
                        ) : null}
                        <div className="dv2-oscore__modal-wheel">
                            <OolaWheelMilestoneV2
                                Fs={selectedFs}
                                celebrate
                                hideFooter
                                hubScore={selected.score}
                            />
                        </div>
                        {selected.socialMediaImageUrl ||
                        selected.socialMediaMessage ? (
                            <button
                                type="button"
                                className="dv2-oscore__modal-share"
                                onClick={() => setShareOpen(true)}
                            >
                                Share
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="dv2-oscore__modal-share"
                                onClick={() => {
                                    setShareOpen(false);
                                    setSelected(null);
                                }}
                            >
                                Close
                            </button>
                        )}
                    </div>
                </div>
            ) : null}

            {shareOpen && selected ? (
                <ShareOptionsModalV2
                    webAlias=""
                    setActiveShare={setShareOpen}
                    imageUrl={selected.socialMediaImageUrl}
                    text={selected.socialMediaMessage}
                />
            ) : null}
        </div>
    );
};

export default OolaScorePanel;
